import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

/**
 * StaffPinModal — vraagt om medewerker + 6-cijferige PIN.
 * Bij succes: onConfirm({ medewerker_user_id, pin, medewerker: {id, name, email} }).
 */
export default function StaffPinModal({ open, onOpenChange, onConfirm, title = "Bevestig met persoonlijke PIN", description }) {
  const [staff, setStaff] = useState([]);
  const [medewerkerId, setMedewerkerId] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setMedewerkerId(""); setPin(""); return; }
    api.get("/staff-pin/available-medewerkers")
      .then(res => setStaff(res.data?.items || []))
      .catch(() => setStaff([]));
  }, [open]);

  const confirm = async () => {
    if (!medewerkerId) return toast.error("Kies je naam uit de lijst");
    if (!/^\d{6}$/.test(pin)) return toast.error("PIN moet exact 6 cijfers zijn");
    setLoading(true);
    try {
      const res = await api.post("/staff-pin/verify", { medewerker_user_id: medewerkerId, pin });
      onConfirm?.({
        medewerker_user_id: medewerkerId,
        pin,
        medewerker: res.data?.medewerker,
      });
      onOpenChange?.(false);
      setMedewerkerId(""); setPin("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ongeldige PIN of medewerker");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="staff-pin-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> {title}
          </DialogTitle>
        </DialogHeader>
        {description && <p className="text-sm text-neutral-600 mb-2">{description}</p>}
        <div className="space-y-3">
          <div>
            <Label>Welke medewerker voert dit uit?</Label>
            <Select value={medewerkerId} onValueChange={setMedewerkerId}>
              <SelectTrigger data-testid="pin-medewerker-select"><SelectValue placeholder="Kies medewerker" /></SelectTrigger>
              <SelectContent>
                {staff.length === 0 && <SelectItem value="_none" disabled>Geen medewerkers met PIN gevonden</SelectItem>}
                {staff.map(m => (
                  <SelectItem key={m.id} value={m.id} data-testid={`pin-medewerker-${m.id}`}>
                    {m.name} {m.email ? `· ${m.email}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-neutral-500 mt-1">
              Zie je jezelf niet? Log in met je persoonlijk medewerker-account en stel je PIN daar in.
            </p>
          </div>
          <div>
            <Label>Persoonlijke PIN (6 cijfers)</Label>
            <Input type="password" value={pin} maxLength={6} inputMode="numeric" placeholder="••••••"
              onChange={e => setPin(e.target.value.replace(/[^0-9]/g, ""))}
              data-testid="pin-input" className="tracking-widest" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Annuleren</Button>
          <Button onClick={confirm} disabled={loading} data-testid="pin-confirm-btn">
            {loading ? "Bezig…" : "Bevestigen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
